import {
  Component,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { InputBase } from 'app/shared/components/input/components/base/input-base';
import { addYears } from 'date-fns';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
})
export class CalendarComponent extends InputBase implements OnChanges {
  placeholder = input<string>('');
  disabledInput = input<boolean>(false);
  dateInput = input<Date | null>(null);
  timeInput = input<Date | null>(null);
  maxDateInput = input<Date>(addYears(new Date(), 100));
  minDateInput = input<Date>(new Date());

  private dateInternal = signal<Date | null>(null);
  private timeInternal = signal<Date | null>(null);

  valueChanges = output<{ date: Date | null; time: Date | null }>();

  get date(): Date | null {
    return this.dateInternal();
  }
  set date(value: Date | null) {
    this.dateInternal.set(value);
    this.emitChange();
  }

  get time(): Date | null {
    return this.timeInternal();
  }
  set time(value: Date | null) {
    this.timeInternal.set(value);
    this.emitChange();
  }

  get disabled(): boolean {
    return this.disabledInput() || this.isDisabled();
  }

  get maxDate(): Date {
    return this.maxDateInput();
  }

  get minDate(): Date {
    return this.minDateInput();
  }

  private cdr = inject(ChangeDetectorRef);

  constructor() {
    super();
  }

  writeValue(value: { date: Date | null; time: Date | null } | null): void {
    if (value) {
      this.dateInternal.set(value.date);
      this.timeInternal.set(value.time);
    } else {
      this.dateInternal.set(null);
      this.timeInternal.set(null);
    }
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateInput']) {
      this.dateInternal.set(this.dateInput());
    }
    if (changes['timeInput']) {
      this.timeInternal.set(this.timeInput());
    }
    this.cdr.markForCheck();
  }

  onDateChange(event: Date | null): void {
    this.date = event;
  }

  onTimeChange(event: Date | null): void {
    this.time = event;
  }

  private emitChange(): void {
    const value = { date: this.date, time: this.time };
    this.valueChanges.emit(value);
    this.onChange(value);
    this.onTouched();
  }

  formatDateInput(event: Event) {
    const inputEl = event.target as HTMLInputElement;
    const start = inputEl.selectionStart ?? 0;

    let value = inputEl.value.replace(/\D/g, '');
    if (value.length > 8) {
      value = value.substring(0, 8);
    }

    let formatted = '';
    if (value.length > 0) {
      formatted = value.substring(0, 2);
    }
    if (value.length >= 3) {
      formatted += '/' + value.substring(2, 4);
    }
    if (value.length >= 5) {
      formatted += '/' + value.substring(4, 8);
    }

    this.updateInput(inputEl, formatted, start);
  }

  formatTimeInput(event: Event) {
    const inputEl = event.target as HTMLInputElement;
    const start = inputEl.selectionStart ?? 0;

    let value = inputEl.value.replace(/\D/g, '');
    if (value.length > 4) value = value.substring(0, 4);

    let formatted = value;
    if (value.length > 2) {
      formatted = [value.substring(0, 2), value.substring(2, 4)].join(':');
    }

    this.updateInput(inputEl, formatted, start);
  }

  private updateInput(inputEl: HTMLInputElement, value: string, start: number) {
    const previousValue = inputEl.value;
    inputEl.value = value;

    const addedChars = value.length - previousValue.length;
    const newPos = Math.max(0, start + addedChars);

    if (value.endsWith(':') && !previousValue.endsWith(':')) {
      inputEl.setSelectionRange(newPos + 1, newPos + 1);
    } else {
      inputEl.setSelectionRange(newPos, newPos);
    }
  }
}
